import { registerAltLabelGenerator, registerDefaultLabelGenerator } from './registry';

// Import all the label generators
import {
  generateOwnerDefaultLabel,
  generateOwnerAltLabels,
} from './generators/ownerLabelGenerator';

import { generatePlanDefaultLabel, generatePlanAltLabels } from './generators/planLabelGenerator';

import {
  generateAdditionalInfoDefaultLabel,
  generateAdditionalInfoAltLabels,
} from './generators/additionalInfoLabelGenerator';

import {
  generateResultDefaultLabel,
  generateResultAltLabels,
} from './generators/resultLabelGenerator';

import {
  generateMilestoneMapDefaultLabel,
  generateMilestoneMapAltLabels,
} from './generators/milestoneMapLabelGenerator';

import {
  generateObjectiveDefaultLabel,
  generateObjectiveAltLabels,
} from './generators/objectiveLabelGenerator';

import {
  generateStatusReportDateDefaultLabel,
  generateStatusReportDateAltLabels,
} from './generators/statusReportDateLabelGenerator';

import {
  generateStatusDefaultLabel,
  generateStatusAltLabels,
} from './generators/statusLabelGenerator';

import {
  generateContainerDefaultLabel,
  generateContainerAltLabels,
} from './generators/containerLabelGenerator';

import {
  generateDependencyDefaultLabel,
  generateDependencyAltLabels,
} from './generators/dependencyLabelGenerator';

/**
 * Register all label generators with the global registry
 *
 * This function should be called once during app initialization
 * to set up all the label generators for the tree view system.
 */
export function registerAllLabelGenerators(): void {
  // console.log('🔧 Registering label generators for tree view...');

  // Owner generators
  registerDefaultLabelGenerator('Owner', generateOwnerDefaultLabel);
  registerAltLabelGenerator('Owner', generateOwnerAltLabels);

  // Plan generators
  registerDefaultLabelGenerator('Plan', generatePlanDefaultLabel);
  registerAltLabelGenerator('Plan', generatePlanAltLabels);

  // Additional Info generators
  registerDefaultLabelGenerator('AdditionalInfo', generateAdditionalInfoDefaultLabel);
  registerAltLabelGenerator('AdditionalInfo', generateAdditionalInfoAltLabels);

  // Result generators (includes KeyResult)
  registerDefaultLabelGenerator('Result', generateResultDefaultLabel);
  registerAltLabelGenerator('Result', generateResultAltLabels);
  registerDefaultLabelGenerator('KeyResult', generateResultDefaultLabel);
  registerAltLabelGenerator('KeyResult', generateResultAltLabels);

  // Milestone Map generators
  registerDefaultLabelGenerator('MilestoneMap', generateMilestoneMapDefaultLabel);
  registerAltLabelGenerator('MilestoneMap', generateMilestoneMapAltLabels);

  // Objective generators (includes MajorObjective)
  registerDefaultLabelGenerator('Objective', generateObjectiveDefaultLabel);
  registerAltLabelGenerator('Objective', generateObjectiveAltLabels);
  registerDefaultLabelGenerator('MajorObjective', generateObjectiveDefaultLabel);
  registerAltLabelGenerator('MajorObjective', generateObjectiveAltLabels);

  // Status Report Date generators
  registerDefaultLabelGenerator('StatusReportDate', generateStatusReportDateDefaultLabel);
  registerAltLabelGenerator('StatusReportDate', generateStatusReportDateAltLabels);

  // Status generators
  registerDefaultLabelGenerator('Status', generateStatusDefaultLabel);
  registerAltLabelGenerator('Status', generateStatusAltLabels);

  // Container generators
  registerDefaultLabelGenerator('Container', generateContainerDefaultLabel);
  registerAltLabelGenerator('Container', generateContainerAltLabels);

  // Dependency generators
  registerDefaultLabelGenerator('Dependency', generateDependencyDefaultLabel);
  registerAltLabelGenerator('Dependency', generateDependencyAltLabels);

  // console.log('✅ Label generators registered successfully');
}

/**
 * Get registration status for debugging
 */
export function getRegistrationStatus(): {
  registeredTypes: string[];
  missingTypes: string[];
  isComplete: boolean;
} {
  const { registeredTypes } = require('./registry').getRegistryStats();

  const expectedTypes = [
    'Owner',
    'Plan',
    'AdditionalInfo',
    'Result',
    'KeyResult',
    'MilestoneMap',
    'Objective',
    'MajorObjective',
    'StatusReportDate',
    'Status',
    'Container',
    'Dependency',
  ];

  const missingTypes = expectedTypes.filter((type) => !registeredTypes.includes(type));

  return {
    registeredTypes,
    missingTypes,
    isComplete: missingTypes.length === 0,
  };
}
